/* Entry motion for the previews of the system.
   - useCountUp: every element marked data-num inside a root counts from 0 to the number it shows, once, when `play`
     turns true. The text is parsed, never changed in format: "19.6 MNOK" → 0.0 … 19.6 MNOK, "58%" → 0 … 58%, "+2.3 MNOK".
     Honours prefers-reduced-motion (then nothing moves). The real app never calls this – only the mockups and the
     proposal's price section do. In-between values step at the number's own precision, and the exact original text is
     always restored at the end (timer + visibilitychange as a safety net when animation frames are throttled). */
import { useEffect } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* "+2.3 MNOK" → { sign: "+", int: "2", sep: ".", frac: "3", rest: " MNOK" } · null when the text is not a plain number */
const parse = (text) => {
  const m = /^(\s*[+−-]?)(\d{1,3}(?:[ \u00a0\u202f]\d{3})+|\d{1,6})(?:([.,])(\d{1,3}))?(\s*[^\d]*)$/.exec(text);
  if (!m) return null;
  const [, sign, int, sep, frac, rest] = m;
  const group = /[ \u00a0\u202f]/.exec(int)?.[0] || "";
  const value = parseFloat(`${int.replace(/[ \u00a0\u202f]/g, "")}.${frac || "0"}`);
  if (!Number.isFinite(value) || value === 0) return null;
  return { sign, sep: sep || "", decimals: frac ? frac.length : 0, rest, value, text, group };
};

/* 1912400 → «1 912 400» with the separator the text used (none when it had none) */
const fmt = (v, p) => {
  const [i, f] = v.toFixed(p.decimals).split(".");
  const int = p.group ? i.replace(/\B(?=(\d{3})+(?!\d))/g, p.group) : i;
  return `${p.sign}${int}${f ? (p.sep || ".") + f : ""}${p.rest}`;
};

const easeOut = (t) => 1 - Math.pow(1 - t, 4);

/* in-between values step at the number's own precision: 14 900 counts in hundreds, 249 000 in thousands – so a
   near-miss like «14 899» never appears, not even for a frame */
const stepOf = (p) => {
  if (p.decimals) return 0;
  let n = Math.round(p.value);
  let step = 1;
  while (step < 1000 && n % 10 === 0 && n > 0) {
    n /= 10;
    step *= 10;
  }
  return step;
};
const snap = (v, step) => (step > 1 ? Math.round(v / step) * step : v);

/* lowest text node inside el that carries the number (data-num sits on the element that shows it) */
const textNode = (el) => {
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let n;
  let found = null;
  while ((n = walk.nextNode())) {
    if (/\d/.test(n.textContent)) found = n;
  }
  return found;
};

export const useCountUp = (ref, play, { delay = 450, duration = 1500, stagger = 80 } = {}, deps = []) => {
  useEffect(() => {
    if (!play) return undefined;
    const root = ref.current;
    if (!root || reduced()) return undefined;
    const items = [];
    root.querySelectorAll("[data-num]").forEach((el, i) => {
      if (el.dataset.counted) return;
      const node = textNode(el);
      if (!node) return;
      const p = parse(node.textContent);
      if (!p) return;
      el.dataset.counted = "1";
      items.push({ el, node, p, step: stepOf(p), start: delay + i * stagger, full: node.textContent });
    });
    if (!items.length) return undefined;
    /* show zero right away so the count visibly starts from nothing */
    items.forEach(({ node, p }) => {
      node.textContent = fmt(0, p);
    });
    let raf = 0;
    let done = false;
    /* the end state is guaranteed: whatever happens to animation frames (background tab, throttling, a slow device),
       every number lands on its exact original text */
    const finish = () => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      items.forEach((it) => {
        if (it.node.textContent !== it.full) it.node.textContent = it.full;
      });
    };
    const t0 = performance.now();
    const tick = (now) => {
      if (done) return;
      let live = false;
      items.forEach((it) => {
        const t = (now - t0 - it.start) / duration;
        if (t >= 1) {
          if (it.node.textContent !== it.full) it.node.textContent = it.full;
          return;
        }
        live = true;
        const v = t <= 0 ? 0 : snap(it.p.value * easeOut(t), it.step);
        it.node.textContent = v >= it.p.value ? it.full : fmt(v, it.p);
      });
      if (live) raf = requestAnimationFrame(tick);
      else done = true;
    };
    raf = requestAnimationFrame(tick);
    const last = Math.max(...items.map((it) => it.start)) + duration;
    const timer = setTimeout(finish, last + 150);
    const onHidden = () => {
      if (document.hidden) finish();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onHidden);
      finish();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, play, delay, duration, stagger, ...deps]);
};
